import { Component } from '@theme/component';
import { normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedProducts extends Component {
  /** @type {Promise<string | null> | null} */
  #activeFetch = null;

  /** @type {boolean} */
  #bootstrapped = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.recentlyViewedLoaded === 'true') {
      return;
    }

    if (this.#bootstrapped) {
      return;
    }

    this.#bootstrapped = true;

    if (document.readyState === 'complete') {
      this.#bootstrap();
      return;
    }

    window.addEventListener('load', this.#bootstrap, { once: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  #bootstrap = () => {
    if (!this.isConnected) return;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) {
      this.hidden = true;
      return;
    }

    this.#loadRecentlyViewedProducts(viewedProducts);
  };

  /**
   * Loads the recently viewed products from the server.
   * @param {string[]} viewedProducts
   */
  #loadRecentlyViewedProducts(viewedProducts) {
    const sectionId = this.dataset.sectionId;

    if (!sectionId) {
      throw new Error('A section ID is required');
    }

    const themeWindow = /** @type {Window & { Theme?: { routes?: { search_url?: string } } }} */ (
      window
    );
    const searchUrl = themeWindow.Theme?.routes?.search_url ?? '/search';
    const url = new URL(searchUrl, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    this.#fetchSectionMarkup(sectionId, url)
      .then((markup) => {
        if (!markup) return;
        if (!this.isConnected) return;

        this.#replaceSection(markup, sectionId);
      })
      .catch((error) => {
        console.error('Recently viewed products error:', error);
      });
  }

  /**
   * Replaces the current section root with fresh server-rendered markup.
   * @param {string} markup
   * @param {string} sectionId
   */
  #replaceSection(markup, sectionId) {
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(markup, 'text/html');
    const normalizedSectionId = normalizeSectionId(sectionId);
    const currentSection = document.getElementById(`shopify-section-${normalizedSectionId}`);
    const newSection = parsedDocument.getElementById(`shopify-section-${normalizedSectionId}`);

    if (!currentSection || !newSection) return;

    newSection.dataset.recentlyViewedLoaded = 'true';
    currentSection.replaceWith(newSection);
  }

  /**
   * Fetches the section markup from the server.
   * @param {string} sectionId
   * @param {URL} url
   * @returns {Promise<string | null>}
   */
  #fetchSectionMarkup(sectionId, url) {
    if (this.#activeFetch) {
      return this.#activeFetch;
    }

    this.#activeFetch = sectionRenderer
      .getSectionHTML(sectionId, false, url)
      .then((markup) => markup ?? null)
      .finally(() => {
        this.#activeFetch = null;
      });

    return this.#activeFetch;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
